
import Stepper from 'primevue/stepper';
import StepList from 'primevue/steplist';
import StepPanels from 'primevue/steppanels';
import StepItem from 'primevue/stepitem';
import Step from 'primevue/step';
import StepPanel from 'primevue/steppanel';

import IftaLabel from 'primevue/iftalabel';
import InputNumber from 'primevue/inputnumber';
import Button from 'primevue/button';
import Message from 'primevue/message';
import ProgressSpinner from 'primevue/progressspinner';
import Toast from 'primevue/toast';

import { computed, defineComponent, onMounted, onUnmounted, ref, watch } from 'vue';
import { useAuthStore } from '../../stores/auth';
import { storeToRefs } from 'pinia';
import { useWebStore } from '../../stores/web';
import { useToast } from 'primevue/usetoast';
import { showVuePopup } from '../../utils/showVuePopup';
import WalletUpdate from '../../popup/WalletUpdate.vue';
import WalletDeposit from '../../popup/WalletDeposit.vue';
import WalletWithdraw from '../../popup/WalletWithdraw.vue';
import AutoPeerNameEn from '../../popup/AutoPeerNameEn.vue';
import { getBankCodeByName, getBankImage } from '../../utils/bank';
import { useSession } from '../../stores/session';
import { depositWithdraw } from '../../apis/deposit_withdraw';
import { AutoPeerDepositStatusResult, AutoPeerPendingResult, AutoPeerTransaction } from '../../types/deposit_withdraw';
import { compressImageToDataURL } from '../../utils/image';

export default defineComponent({
	name: 'DepositAutoPeer',
	components: {
		Stepper, StepList, StepPanels, StepItem, Step, StepPanel,
		IftaLabel, InputNumber,
		Button, Message, ProgressSpinner, Toast,
	},
	setup() {
		const auth = useAuthStore();
		auth.hydrate();
		const { profile } = storeToRefs(auth);

		const web = useWebStore();
		web.hydrate();
		const { webPermission } = storeToRefs(web);

		const toast = useToast();
		const session = useSession();

		const stepper = ref<string>('0');
		const resetKey = ref(0);
		const fileInputRef = ref<HTMLInputElement | null>(null);
		const isDragging = ref(false);

		const isActive = computed<boolean>(() => webPermission.value?.isDeposit?.isAutoPeer ?? false);

		const isBankUpdate = computed<boolean>(() => {
			const hasAcc = !!profile.value?.customerBankAccount;
			const hasName = !!profile.value?.customerBankName;
			return !(hasAcc && hasName);
		});

		const isNameEnUpdate = computed<boolean>(() => {
			return !profile.value?.firstNameEn || !profile.value?.lastNameEn;
		});

		const msgErr = ref('');
		const depositRef = ref('');
		const pendingLoading = ref(false);
		const pendingList = ref<AutoPeerTransaction[]>([]);
		const pendingSummary = ref('');

		const form = ref({
			amount: 0,
			imgSlip: '',
			btn: { disabled: false, loading: false },
		});

		const promptPayQR = ref('');

		// ปุ่มลัดเลือกจำนวนเงินฝาก
		const amountPresets = [100, 300, 500, 1000, 3000, 5000, 10000, 20000];

		// error code จาก /p2c/deposit ที่ให้สลับลูกค้าไปฝากแนบสลิปแทน
		const SWITCH_TO_SLIP_CODES = ['60001', '88001', '88002', '88004', '88005', '99000', '99001', '99002', '99003'];

		// Polling interval for deposit-status
		let statusInterval: ReturnType<typeof setInterval> | null = null;

		// code 50016: สลิปอยู่ระหว่างการตรวจสอบ — ถือว่ารับเรื่องแล้ว รีเซ็ตฟอร์มกลับ (ดักทุก AutoPeer response)
		const handleUnderReview = (resp: any): boolean => {
			if (String(resp?.code) !== '50016') return false;
			clearStatusPolling();
			clearCountdown();
			resetForm();
			toast.add({ severity: 'info', summary: 'สลิปอยู่ระหว่างการตรวจสอบ', life: 4000 });
			return true;
		};

		const resetForm = () => {
			msgErr.value = '';
			depositRef.value = '';
			promptPayQR.value = '';
			activeTx.value = null;
			form.value = {
				amount: 0,
				imgSlip: '',
				btn: { disabled: false, loading: false },
			};
			if (fileInputRef.value) fileInputRef.value.value = '';
			isDragging.value = false;
		};

		const clearStatusPolling = () => {
			if (statusInterval) {
				clearInterval(statusInterval);
				statusInterval = null;
			}
		};

		const activeTx = ref<AutoPeerTransaction | null>(null);

		// ไอคอนธนาคารปลายทาง (รองรับทั้ง bankCode และชื่อธนาคารภาษาไทย)
		const bankIcon = computed(() => {
			const tx = activeTx.value as any;
			if (!tx) return '';
			return getBankImage(tx.bankCode || getBankCodeByName(tx.bankName));
		});

		// ซ่อนไอคอนถ้าโหลดรูปไม่สำเร็จ (บางธนาคารไม่มีไฟล์บน storage)
		const bankIconError = ref(false);

		const applyTransaction = (tx: AutoPeerTransaction) => {
			activeTx.value = tx;
			depositRef.value = tx.ref;
			promptPayQR.value = tx.promptPayQR ?? '';
			form.value.amount = Number(tx.amount) || 0;
			bankIconError.value = false;
		};

		// ===== นับถอยหลังเวลาทำรายการ (real-time) =====
		const nowTs = ref(Date.now());
		let countdownInterval: ReturnType<typeof setInterval> | null = null;

		const clearCountdown = () => {
			if (countdownInterval) {
				clearInterval(countdownInterval);
				countdownInterval = null;
			}
		};

		const remainingMs = computed(() => {
			if (!activeTx.value?.expiredDate) return 0;
			const exp = new Date(activeTx.value.expiredDate).getTime();
			if (Number.isNaN(exp)) return 0;
			return Math.max(0, exp - nowTs.value);
		});

		const isExpired = computed(() => !!activeTx.value?.expiredDate && remainingMs.value <= 0);

		const countdownText = computed(() => {
			const totalSec = Math.floor(remainingMs.value / 1000);
			const m = Math.floor(totalSec / 60);
			const s = totalSec % 60;
			return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
		});

		const startCountdown = () => {
			clearCountdown();
			nowTs.value = Date.now();
			countdownInterval = setInterval(() => {
				nowTs.value = Date.now();
				if (remainingMs.value <= 0) clearCountdown();
			}, 1000);
		};

		// ดึง pending list จาก result (ใช้ร่วมกันหลาย flow)
		const applyPendingResult = (result: any) => {
			const data = result as AutoPeerPendingResult;
			if (!data?.list || data.list.length === 0) return false;
			pendingList.value = data.list;
			pendingSummary.value = data.summary ?? '';
			return true;
		};

		// Step 1: Check pending deposit
		const checkPending = async () => {
			pendingLoading.value = true;
			pendingList.value = [];
			pendingSummary.value = '';
			try {
				const resp = await depositWithdraw.autoPeerDepositPending({ data: null });

				if (resp.code === '8017') {
					alert(resp.message);
					await auth.logout();
					window.location.reload();
					return;
				}

				if (handleUnderReview(resp)) return;

				// ทั้ง code '0' และ code อื่นที่มี result.list → แสดง pending
				if (resp.result) {
					applyPendingResult(resp.result);
				}
			} catch {
				// ไม่มี pending ก็ไม่ error
			} finally {
				pendingLoading.value = false;
			}
		};

		// เลือกรายการค้าง → ไป step 2
		const selectPending = (tx: AutoPeerTransaction) => {
			applyTransaction(tx);
			stepper.value = '2';
		};

		// ยกเลิกรายการค้างจาก list
		const cancelPendingItem = async (tx: AutoPeerTransaction) => {
			form.value.btn.loading = true;
			try {
				const resp = await depositWithdraw.autoPeerCancelTransfer({ ref: [tx.ref] });

				if (resp.code === '8017') {
					alert(resp.message);
					await auth.logout();
					window.location.reload();
					return;
				}

				if (handleUnderReview(resp)) return;
				if (resp.code !== '0') throw new Error(resp.message);

				// ลบออกจาก list
				pendingList.value = pendingList.value.filter((item) => item.ref !== tx.ref);
				toast.add({ severity: 'warn', summary: 'ยกเลิกรายการแล้ว', detail: 'คุณจะถูกระงับการฝากช่องทาง AutoPeer 3 นาที', life: 5000 });
			} catch (error: any) {
				toast.add({ severity: 'error', summary: 'เกิดข้อผิดพลาดบางอย่าง', detail: error?.message || String(error), life: 3000 });
			} finally {
				form.value.btn.loading = false;
			}
		};

		// จบรายการค้างจาก list
		const forceCompletePendingItem = async (tx: AutoPeerTransaction) => {
			form.value.btn.loading = true;
			try {
				const resp = await depositWithdraw.autoPeerForceComplete({ ref: [tx.ref] });

				if (resp.code === '8017') {
					alert(resp.message);
					await auth.logout();
					window.location.reload();
					return;
				}

				if (handleUnderReview(resp)) return;
				if (resp.code !== '0') throw new Error(resp.message);

				pendingList.value = pendingList.value.filter((item) => item.ref !== tx.ref);
				toast.add({ severity: 'success', summary: 'จบรายการสำเร็จ', life: 2000 });
			} catch (error: any) {
				toast.add({ severity: 'error', summary: 'เกิดข้อผิดพลาดบางอย่าง', detail: error?.message || String(error), life: 3000 });
			} finally {
				form.value.btn.loading = false;
			}
		};

		// Create deposit transaction
		const handleCreateDeposit = async () => {
			msgErr.value = '';
			form.value.btn.loading = true;

			if (form.value.amount <= 0) {
				msgErr.value = 'กรุณาระบุจำนวนเงิน';
				form.value.btn.loading = false;
				return;
			}

			if (form.value.amount < 100 || form.value.amount % 100 !== 0) {
				msgErr.value = 'ใส่จำนวนเต็มร้อยเท่านั้น เช่น 100 , 5000 , 10000 เป็นต้น';
				form.value.btn.loading = false;
				return;
			}

			if (form.value.amount > 20000) {
				msgErr.value = 'ฝากสูงสุด 20,000 บาท';
				form.value.btn.loading = false;
				return;
			}

			try {
				const resp = await depositWithdraw.autoPeerDeposit({ amount: form.value.amount });

				if (resp.code === '8017') {
					alert(resp.message);
					await auth.logout();
					window.location.reload();
					return;
				}

				if (handleUnderReview(resp)) return;

				// error code กลุ่มนี้ → สลับลูกค้าไปฝากแนบสลิปแทน (เงียบ ๆ ไม่แสดง error)
				if (SWITCH_TO_SLIP_CODES.includes(String(resp.code))) {
					// lockTab: ห้าม autopeer-first ใน WalletDeposit ทับแท็บนี้ ไม่งั้นลูกค้า
					// ถูกเด้งกลับมาที่ AutoPeer ที่เพิ่ง error
					await showVuePopup(WalletDeposit, { tabs: 'slip', lockTab: true });
					return;
				}

				// มีรายการค้างอยู่ → แสดง pending list
				if (resp.code !== '0' && resp.result?.list) {
					applyPendingResult(resp.result);
					toast.add({ severity: 'warn', summary: resp.message, life: 3000 });
					return;
				}

				if (resp.code !== '0') throw new Error(resp.message);

				const result = resp.result as AutoPeerPendingResult;
				if (!result?.list || result.list.length === 0) {
					throw new Error('ไม่พบข้อมูลรายการฝาก');
				}

				applyTransaction(result.list[0]);
				stepper.value = '2';
			} catch (error: any) {
				msgErr.value = error?.message || String(error);
				toast.add({ severity: 'error', summary: 'เกิดข้อผิดพลาดบางอย่าง', detail: msgErr.value, life: 3000 });
			} finally {
				form.value.btn.loading = false;
			}
		};

		// Poll deposit status
		const pollDepositStatus = async () => {
			try {
				const resp = await depositWithdraw.autoPeerDepositStatus({ data: null });

				if (resp.code === '8017') {
					alert(resp.message);
					await auth.logout();
					window.location.reload();
					return;
				}

				if (handleUnderReview(resp)) return;
				if (resp.code !== '0') return;

				const result = resp.result as AutoPeerDepositStatusResult;
				if (!result?.list || result.list.length === 0) return;

				const tx = result.list.find((item) => item.ref === depositRef.value);
				if (!tx) return;

				if (tx.status === 'SUCCESS' || tx.status === 'COMPLETED') {
					clearStatusPolling();
					stepper.value = '3';
					toast.add({ severity: 'success', summary: 'ฝากสำเร็จ', detail: 'ยอดเงินถูกเติมเข้าระบบแล้ว', life: 3000 });
				}
			} catch {
				// ignore polling errors
			}
		};

		const startPolling = () => {
			clearStatusPolling();
			statusInterval = setInterval(pollDepositStatus, 5000);
		};

		// Upload slip
		const processFile = async (file?: File) => {
			if (!file) {
				toast.add({ severity: 'warn', summary: 'กรุณาเลือกไฟล์ก่อน', life: 2000 });
				return;
			}

			// รับทุกชนิดรูป — compressImageToDataURL จะ re-encode เป็น jpeg ให้ก่อนส่ง
			// (สลิปบนมือถือเป็น .webp บ่อย บล็อกตรงนี้เท่ากับลูกค้าฝากเงินไม่ได้)
			if (!file.type.startsWith('image/')) {
				toast.add({ severity: 'warn', summary: 'ไฟล์ต้องเป็นรูปภาพเท่านั้น', life: 2000 });
				return;
			}

			if (file.size > 10 * 1024 * 1024) {
				toast.add({ severity: 'warn', summary: 'ไฟล์ใหญ่เกิน 10MB', life: 2000 });
				return;
			}

			form.value.btn.loading = true;

			try {
				// ย่อ + บีบก่อนเข้า base64 — สลิปจากมือถือดิบ ๆ ทำให้ payload พองเป็นสิบ MB
				form.value.imgSlip = await compressImageToDataURL(file);
				toast.add({ severity: 'info', summary: 'อ่านไฟล์สำเร็จ', life: 1500 });
			} catch {
				toast.add({ severity: 'error', summary: 'อ่านไฟล์ไม่สำเร็จ', life: 2000 });
			} finally {
				form.value.btn.loading = false;
			}
		};

		const triggerFilePick = () => {
			if (isExpired.value) return;
			fileInputRef.value?.click();
		};

		const onFileChange = (event: Event) => {
			const input = event.target as HTMLInputElement;
			processFile(input.files?.[0]);
			input.value = '';
		};

		const onDrop = (event: DragEvent) => {
			isDragging.value = false;
			if (isExpired.value) return;
			processFile(event.dataTransfer?.files?.[0]);
		};

		const clearSlip = () => {
			form.value.imgSlip = '';
			if (fileInputRef.value) fileInputRef.value.value = '';
		};

		const handleUploadSlip = async () => {
			msgErr.value = '';
			form.value.btn.loading = true;

			if (!form.value.imgSlip) {
				msgErr.value = 'กรุณาเลือกรูปสลิปฝาก';
				form.value.btn.loading = false;
				return;
			}

			try {
				const resp = await depositWithdraw.autoPeerUploadSlip({
					imgSlip: form.value.imgSlip,
					ref: depositRef.value,
				});

				if (resp.code === '8017') {
					alert(resp.message);
					await auth.logout();
					window.location.reload();
					return;
				}

				if (handleUnderReview(resp)) return;
				if (resp.code !== '0') throw new Error(resp.message);

				clearStatusPolling();
				clearCountdown();
				resetForm();
				toast.add({ severity: 'success', summary: 'อัปโหลดสลิปสำเร็จ', detail: 'รอระบบตรวจสอบ', life: 2000 });

				// ยืนยันแล้วกลับไปหน้าถอน
				session.set('navbar_sub', 'wallet:withdraw');
				await showVuePopup(WalletWithdraw);
			} catch (error: any) {
				msgErr.value = error?.message || String(error);
				toast.add({ severity: 'error', summary: 'เกิดข้อผิดพลาดบางอย่าง', detail: msgErr.value, life: 3000 });
			} finally {
				form.value.btn.loading = false;
			}
		};

		// Force complete (step 2/3)
		const handleForceComplete = async () => {
			form.value.btn.loading = true;
			try {
				const resp = await depositWithdraw.autoPeerForceComplete({ ref: [depositRef.value] });

				if (resp.code === '8017') {
					alert(resp.message);
					await auth.logout();
					window.location.reload();
					return;
				}

				if (handleUnderReview(resp)) return;
				if (resp.code !== '0') throw new Error(resp.message);

				clearStatusPolling();
				stepper.value = '3';
				toast.add({ severity: 'success', summary: 'จบรายการสำเร็จ', life: 2000 });
			} catch (error: any) {
				toast.add({ severity: 'error', summary: 'เกิดข้อผิดพลาดบางอย่าง', detail: error?.message || String(error), life: 3000 });
			} finally {
				form.value.btn.loading = false;
			}
		};

		// Cancel transfer (step 2/3)
		const handleCancelTransfer = async () => {
			form.value.btn.loading = true;
			try {
				const resp = await depositWithdraw.autoPeerCancelTransfer({ ref: [depositRef.value] });

				if (resp.code === '8017') {
					alert(resp.message);
					await auth.logout();
					window.location.reload();
					return;
				}

				if (handleUnderReview(resp)) return;
				if (resp.code !== '0') throw new Error(resp.message);

				clearStatusPolling();
				resetForm();
				stepper.value = '0';
				resetKey.value++;
				toast.add({ severity: 'warn', summary: 'ยกเลิกรายการแล้ว', detail: 'คุณจะถูกระงับการฝากช่องทาง AutoPeer 3 นาที', life: 5000 });
			} catch (error: any) {
				toast.add({ severity: 'error', summary: 'เกิดข้อผิดพลาดบางอย่าง', detail: error?.message || String(error), life: 3000 });
			} finally {
				form.value.btn.loading = false;
			}
		};

		const formatDate = (dateStr: string) => {
			if (!dateStr) return '';
			const d = new Date(dateStr);
			return d.toLocaleString('th-TH', { dateStyle: 'short', timeStyle: 'short' });
		};

		onMounted(() => {
			auth.hydrate();
			web.hydrate();
		});

		onUnmounted(() => {
			clearStatusPolling();
			clearCountdown();
		});

		watch(stepper, (value) => {
			if (value === '0') {
				clearStatusPolling();
				clearCountdown();
				resetForm();
				resetKey.value++;
			}
			if (value === '1') {
				checkPending();
			}
			if (value === '2') {
				startPolling();
				startCountdown();
			}
			if (value === '3') {
				clearStatusPolling();
				clearCountdown();
				resetForm();
			}
		});

		const copyPromptPay = () => {
			navigator.clipboard.writeText(promptPayQR.value);
			toast.add({ severity: 'info', summary: 'คัดลอกแล้ว', life: 1500 });
		};

		const copyToClipboard = (text: string) => {
			navigator.clipboard.writeText(text);
			toast.add({ severity: 'info', summary: 'คัดลอกแล้ว', life: 1500 });
		};

		const btnClickUpdateBank = async () => {
			session.set('navbar_primary', 'wallet');
			session.set('navbar_sub', 'wallet:update');
			await showVuePopup(WalletUpdate, { tabs: 'bank' });
		};

		const btnClickUpdateNameEn = async () => {
			await showVuePopup(
				AutoPeerNameEn,
				{
					// อัปเดตชื่อ EN เสร็จแล้วเปิด popup ฝาก AutoPeer กลับมาเหมือนเดิม
					onSuccess: () => showVuePopup(WalletDeposit, { tabs: 'autopeer' }),
				},
				{ customClass: { htmlContainer: 'min-h-fit!' } }
			);
		};

		const resetToStep1 = () => {
			clearStatusPolling();
			resetForm();
			stepper.value = '0';
			resetKey.value++;
		};

		return {
			profile,
			stepper,
			form,
			msgErr,
			activeTx,
			depositRef,
			pendingLoading,
			pendingList,
			pendingSummary,
			promptPayQR,
			amountPresets,
			isActive,
			isBankUpdate,
			isNameEnUpdate,
			btnClickUpdateBank,
			btnClickUpdateNameEn,
			fileInputRef,
			isDragging,
			bankIcon,
			bankIconError,
			resetKey,
			resetToStep1,
			handleCreateDeposit,
			selectPending,
			cancelPendingItem,
			forceCompletePendingItem,
			triggerFilePick,
			onFileChange,
			onDrop,
			clearSlip,
			handleUploadSlip,
			handleForceComplete,
			handleCancelTransfer,
			copyPromptPay,
			copyToClipboard,
			formatDate,
			remainingMs,
			isExpired,
			countdownText,
		};
	},
});
